import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from 'contexts/AuthContext';
import { userTours } from 'services/api';
import type { UserTourProgressRecord } from 'services/api';

type UseUserTourProgressResult = {
  hasSeen: boolean;
  isLoading: boolean;
  status: UserTourProgressRecord['status'] | null;
  record: UserTourProgressRecord | null;
  markDismissed: () => Promise<UserTourProgressRecord>;
  markCompleted: () => Promise<UserTourProgressRecord>;
};

export const useUserTourProgress = (tourKey: string): UseUserTourProgressResult => {
  const { user } = useAuth();
  const [record, setRecord] = useState<UserTourProgressRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    if (!user?.id) {
      setRecord(null);
      setIsLoading(false);
      return () => {
        isActive = false;
      };
    }

    setIsLoading(true);
    userTours
      .list()
      .then((records) => {
        if (!isActive) {
          return;
        }
        const nextRecord = records.find((candidate) => candidate.tour_key === tourKey) ?? null;
        setRecord(nextRecord);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setRecord(null);
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [tourKey, user?.id]);

  const updateStatus = useCallback(
    async (status: UserTourProgressRecord['status']) => {
      const updatedRecord = await userTours.update(tourKey, status);
      setRecord(updatedRecord);
      return updatedRecord;
    },
    [tourKey],
  );

  const markDismissed = useCallback(() => updateStatus('dismissed'), [updateStatus]);
  const markCompleted = useCallback(() => updateStatus('completed'), [updateStatus]);

  const status = record?.status ?? null;
  const hasSeen = useMemo(() => Boolean(record), [record]);

  return {
    hasSeen,
    isLoading,
    status,
    record,
    markDismissed,
    markCompleted,
  };
};
